import { HeaderPositionType, TextStyleType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

const SHAPE_ORDER = ["diamond", "gear", "puzzle", "hexagon", "circle"];

export class SequenceCategory extends BaseCategory {
    get name() {
        return "Sequence Diagram";
    }

    get type() {
        return SlideCategories.SEQUENCE_DIAGRAM;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content describes an ordered sequence of ideas or stages (2-10 items) that should be shown as connected sequence shapes. Use for concise sequence steps, not long-form paragraphs. Prefer this category for sequence visuals such as gears, puzzle, diamond, hexagon, and circles/rings.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Parse the content into an ordered sequence of 2-10 concise items. Keep each item short and idea-focused. Include optional short supporting text per item and place any extra summary text into conclusion.
If the user implies a sequence shape, set preferredShapeType to one of: diamond, gear, puzzle, hexagon, circle. If no clear shape is implied, set preferredShapeType to "none".`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema(options = {}) {
        return {
            items: this.getItemListSchema(options),
            preferredShapeType: "none | diamond | gear | puzzle | hexagon | circle",
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        return this.validateArray(model.items);
    }

    getSlideModels(model) {
        const normalizedModel = this.normalizeModel(model);
        const preferredShapeType = this.getPreferredShapeType(normalizedModel);
        const orderedShapeTypes = this.getOrderedShapeTypes(preferredShapeType);

        return this.buildModels(orderedShapeTypes.map((shapeType, idx) => (
            this.buildSequenceModel(this.getTemplateForShape(shapeType), normalizedModel, shapeType, idx === 0)
        )));
    }

    normalizeModel(model) {
        const items = (model.items || []).slice(0, 10);
        return {
            ...model,
            items,
        };
    }

    getPreferredShapeType(model) {
        const fromModel = this.normalizeShapeType(model.preferredShapeType);
        if (fromModel && fromModel !== "none") {
            return fromModel;
        }

        // Fallback heuristic if the model does not set a preferred shape.
        const tokens = [model.slideTitle, model.userPrompt, model.prompt]
            .filter(value => typeof value === "string" && value.trim().length > 0)
            .join(" ")
            .toLowerCase()
            .split(/[^a-z]+/)
            .filter(Boolean);
        const tokenSet = new Set(tokens);

        if (["gear", "gears", "cog", "cogs"].some(term => tokenSet.has(term))) {
            return "gear";
        }
        if (["puzzle", "puzzles", "jigsaw"].some(term => tokenSet.has(term))) {
            return "puzzle";
        }
        if (["hexagon", "hexagons", "honeycomb"].some(term => tokenSet.has(term))) {
            return "hexagon";
        }
        if (["circle", "circles", "ring", "rings"].some(term => tokenSet.has(term))) {
            return "circle";
        }
        if (["diamond", "diamonds", "rhombus"].some(term => tokenSet.has(term))) {
            return "diamond";
        }

        return null;
    }

    normalizeShapeType(shapeType) {
        if (typeof shapeType !== "string") {
            return null;
        }

        const normalized = shapeType.trim().toLowerCase();
        if (normalized === "gears" || normalized === "cog" || normalized === "cogs") {
            return "gear";
        }
        if (normalized === "puzzles" || normalized === "jigsaw") {
            return "puzzle";
        }
        if (normalized === "diamonds" || normalized === "rhombus") {
            return "diamond";
        }
        if (normalized === "hexagons") {
            return "hexagon";
        }
        if (normalized === "circles" || normalized === "ring" || normalized === "rings") {
            return "circle";
        }
        if (normalized === "none" || SHAPE_ORDER.includes(normalized)) {
            return normalized;
        }
        return null;
    }

    getOrderedShapeTypes(preferredShapeType) {
        if (!preferredShapeType || !SHAPE_ORDER.includes(preferredShapeType)) {
            return [...SHAPE_ORDER];
        }

        return [
            preferredShapeType,
            ...SHAPE_ORDER.filter(shape => shape !== preferredShapeType),
        ];
    }

    getTemplateForShape(shapeType) {
        if (shapeType === "gear") {
            return "gears";
        }
        if (shapeType === "puzzle") {
            return "puzzle";
        }
        return "sequence";
    }

    buildSequenceModel(templateId, model, shapeType, isFirstChoice) {
        const builder = new SlideModelBuilder(templateId, model, isFirstChoice);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        builder.createPrimary({
            collectionColor: "colorful",
            shapeType,
            ornamentType: "none",
            items: model.items.map(item => ({
                text: {
                    blocks: [
                        builder.createBlock(item.title, TextStyleType.TITLE),
                        builder.createBlock(item.description, TextStyleType.BODY),
                    ].filter(Boolean),
                },
            })),
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder;
    }
}
